/**
 * Fait : la partie « Les invitations » de l'écran d'administration — inviter
 *   une adresse, voir ce que chaque invitation est devenue, en annuler une.
 *
 *   Quand le courriel n'est pas parti, LE LIEN S'AFFICHE : dire « transmettez
 *   le lien vous-même » sans le donner laisserait une invitation créée et
 *   introuvable, car il n'est écrit nulle part ailleurs.
 *
 * Ne fait pas : elle n'appelle pas l'API — le parent le fait et recharge — et
 *   ne crée aucun compte : c'est l'invité qui le fait, en suivant son lien.
 */

import { ROLES_DE_COMPTE, type RoleDeCompte } from '@aymoai/shared';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import type { InvitationVue } from './api.ts';
import { NOM_DU_ROLE } from './SectionDeLaFamille.tsx';

/** L'état d'une invitation, dit en français. */
const NOM_DE_L_ETAT: Readonly<Record<InvitationVue['etat'], string>> = {
  active: 'en attente',
  utilisee: 'compte créé',
  revoquee: 'annulée',
  expiree: 'expirée',
};

const AIDE =
  'La personne invitée reçoit un courriel avec un lien. Elle choisit son mot ' +
  'de passe, et la voilà dans l’atelier.';

/** Une date, écrite en toutes lettres — jamais un format de machine. */
function enFrancais(quand: string): string {
  return new Date(quand).toLocaleDateString('fr-FR', { dateStyle: 'long' });
}

export function SectionDesInvitations({
  invitations,
  echec,
  adresseInvitee,
  roleAttribue,
  lienARecopier,
  enCours,
  surAdresse,
  surRole,
  surEnvoi,
  surAnnulation,
}: {
  readonly invitations: readonly InvitationVue[];

  /**
   * Pourquoi la liste manque, quand elle manque. Sans lui, un échec s'affichait
   * en « Aucune invitation en attente » : l'écran affirmait un fait qu'il
   * n'avait pas pu vérifier.
   */
  readonly echec: string | null;
  readonly adresseInvitee: string;
  readonly roleAttribue: RoleDeCompte;

  /** Montré seulement quand le courriel n'a pas pu partir. */
  readonly lienARecopier: string | null;

  readonly enCours: boolean;
  surAdresse: (adresse: string) => void;
  surRole: (role: RoleDeCompte) => void;
  surEnvoi: (evenement: React.FormEvent) => void;
  surAnnulation: (invitation: InvitationVue) => void;
}) {
  const enAttente = invitations.filter((une) => une.etat === 'active');

  return (
    <Partie
      etiquette="Faire entrer quelqu’un"
      nature="partage"
      titre="Les invitations"
      niveauDeTitre={2}
      aide={AIDE}
      data-role="partie-invitations"
      // L'état de CETTE partie, qui a un vide de plein droit : aucune
      // invitation envoyée n'est un état normal, pas une anomalie.
      data-etat={echec !== null ? 'erreur' : invitations.length === 0 ? 'vide' : 'nominal'}
    >
      <form onSubmit={surEnvoi} data-role="invitation">
        <Champ
          libelle="Adresse électronique de la personne"
          name="adresse-invitee"
          type="email"
          value={adresseInvitee}
          onChange={(evenement) => surAdresse(evenement.target.value)}
        />

        <p className="champ">
          <label htmlFor="role-attribue">Ce qu’elle pourra faire</label>
          <select
            id="role-attribue"
            name="role-attribue"
            value={roleAttribue}
            onChange={(evenement) => surRole(evenement.target.value as RoleDeCompte)}
          >
            {ROLES_DE_COMPTE.map((role) => (
              <option key={role} value={role}>
                {NOM_DU_ROLE[role]}
              </option>
            ))}
          </select>
        </p>

        <button
          type="submit"
          data-role="principal"
          disabled={enCours || adresseInvitee.trim() === ''}
        >
          {enCours ? 'Envoi…' : 'Envoyer l’invitation'}
        </button>
      </form>

      {lienARecopier !== null && (
        <p data-role="lien-d-invitation">
          Le lien à transmettre : <code>{lienARecopier}</code>
        </p>
      )}

      {echec !== null && (
        <Annonce urgence="urgent" nature="refus">
          {echec}
        </Annonce>
      )}

      <ul className="rangees" data-liste="invitations">
        {/*
         * Le vide se dit sur la liste ENTIÈRE, pas sur les seules invitations
         * en attente : la version d'avant affichait « Aucune invitation en
         * attente » juste au-dessus d'invitations déjà utilisées, ce qui se
         * lisait comme une contradiction.
         */}
        {echec === null && invitations.length === 0 && (
          <li data-etat="vide">Aucune invitation envoyée pour l’instant.</li>
        )}
        {enAttente.length === 0 && invitations.length > 0 && (
          <li data-role="aucune-en-attente">Aucune invitation n’est en attente.</li>
        )}
        {invitations.map((une) => (
          <li key={une.id} data-invitation={une.id} data-etat-invitation={une.etat}>
            <strong>{une.adresseInvitee}</strong>
            <span className="texte-attenue">— {NOM_DE_L_ETAT[une.etat]}</span>
            <span className="texte-attenue">
              {une.etat === 'active'
                ? `valable jusqu’au ${enFrancais(une.expireLe)}`
                : `du ${enFrancais(une.creeLe)}`}
            </span>

            {une.etat === 'active' && (
              <button
                type="button"
                data-role="annuler-invitation"
                disabled={enCours}
                onClick={() => surAnnulation(une)}
              >
                Annuler
              </button>
            )}
          </li>
        ))}
      </ul>
    </Partie>
  );
}
